//https://github.com/ckshekhar73/react-semantic-redux-form/blob/master/src/index.js
import React, { Fragment } from 'react';
import {
  Button,
  Form,
  Input as InputComponent,
  Radio as RadioComponent,
  Checkbox as CheckboxComponent,
  Select as SelectComponent,
  TextArea as TextAreaComponent,
  Dropdown as DropdownComponent,
  Grid,
  Popup,
  Icon,
} from 'semantic-ui-react';
import _ from 'lodash';
import DatePicker from 'react-datepicker';

import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Field, change as reduxFormChange } from 'redux-form';
import { isKeyHotkey } from 'is-hotkey';

import {
  formParseInt,
  formParseDecimal,
  convertMomentToUnicode,
  convertMomentPropsToDateProps,
} from 'Common/FormHelper';
import { InputMode } from 'Common/FormEnum';
import { DATE_FORMAT_FOR_UI, Y_N_BIT } from 'Common/Constants';
import { ImageUploader } from 'Common/Components/ImageUploader/ImageUploader';
import { RelativePortal } from 'Common/Components';
import {
  FractionalEnum,
  FractionalDropdownOptions,
} from 'Common/Constants/Enumeration/FractionalEnum';
import { add } from 'Common/Helpers/currency';

import {
  getValueFromInputMode,
  handleModeInputKeyPress,
} from './GeneralHelper';

import 'react-datepicker/dist/react-datepicker.css';

export { ReduxFormButtonGroup } from 'Components/forms/ReduxFormButtonGroup';

export const TextLabelField = (props) => {
  const inlineLabel = props.inlineLabel ? props.inlineLabel + ': ' : null;
  return (
    <Fragment>
      {props.label && !inlineLabel && <label>{props.label}</label>}
      {inlineLabel} {props.input.value}
    </Fragment>
  );
};

export const dateRegExp = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
export const dateRegExpMonthYear = /^\d{1,2}\/\d{4}$/;

export class DatePickerField extends React.Component {
  static propTypes = {
    input: PropTypes.shape({
      onChange: PropTypes.func.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
    meta: PropTypes.shape({
      touched: PropTypes.bool,
      error: PropTypes.string,
      warning: PropTypes.string,
    }),
    placeholder: PropTypes.string,
    zIndex: PropTypes.number,
    fluid: PropTypes.bool,
    dateFormat: PropTypes.string,
    formatOnBlur: PropTypes.bool,
    showMonthDropdown: PropTypes.bool,
    showYearDropdown: PropTypes.bool,
    scrollableYearDropdown: PropTypes.bool,
    openToDate: PropTypes.object,
    minDate: PropTypes.object,
    maxDate: PropTypes.object,
    onChange: PropTypes.func,
    strictParsing: PropTypes.bool,
    style: PropTypes.object,
  };

  static defaultProps = {
    placeholder: '',
    zIndex: null,
    fluid: false,
    dateFormat: DATE_FORMAT_FOR_UI,
    formatOnBlur: true,
    showMonthDropdown: true,
    showYearDropdown: true,
    scrollableYearDropdown: true,
    onChange: (date) => {},
    strictParsing: true, // needed for backwards compatible with moment date formats
    style: {},
  };

  /**
   * Handles user input
   */
  handleChange = (e) => {
    const { input } = this.props;
    if (e && e.target) {
      const dateString = e.target.value.replace(/[^\d/]/g, '').substring(0, 10);
      input.onChange(dateString);
    } else {
      input.onChange(e);
    }
  };

  onChange = (_date) => {
    const { dateFormat, input, onChange } = this.props;
    let formattedDate = '';
    const date = moment(_date, dateFormat);
    if (date.isValid()) {
      formattedDate = date.format(dateFormat);
    }
    onChange(formattedDate);
    input.onChange(formattedDate);
  };

  onBlur = (e) => {
    const { input, dateFormat, formatOnBlur } = this.props;
    const date = moment(input.value, dateFormat, true);

    if (!date.isValid()) {
      this.onChange('');
      input.onBlur(e);
      return;
    }

    if (formatOnBlur) {
      this.onChange(input.value);
    }
    input.onBlur(e);
  };

  onFocus = (e) => {
    const { input } = this.props;
    input.onFocus(e);
  };

  onSelect = (e) => {
    const { dateFormat } = this.props;
    const date = moment(e, dateFormat, true);
    const val = date.isValid() ? date : '';
    this.onChange(val);
  };

  get selected() {
    const { input, dateFormat } = this.props;
    const date = moment(input.value, dateFormat);
    if (input.value && date.isValid()) {
      return date.toDate();
    }
    return null;
  }

  getStyle = () => {
    const { zIndex, style } = this.props;
    const styleObj = {
      width: 'auto',
      ...style,
    };

    if (zIndex) {
      styleObj.zIndex = zIndex;
    }

    return styleObj;
  };

  /**
   * DatePicker now uses date-fns instead of moment
   * Converts usage of moment's date custom formatting to unicode
   */
  get dateFormat() {
    const { dateFormat } = this.props;
    return convertMomentToUnicode(dateFormat);
  }

  render() {
    const {
      input,
      required,
      meta: { touched, error },
      label,
      tabIndex,
      className,
      size,
      placeholder,
      customInput,
      disabled,
      dateFormat,
      fluid,
      onChange,
      strictParsing,
      style,

      ...rest
    } = convertMomentPropsToDateProps(this.props);

    const { onChange: inputOnChange, ...other } = input;
    const classes = classNames(
      'ui',
      'input',
      { small: size === 'small' },
      { 'fluid date-picker-fluid': fluid }
    );

    return (
      <Form.Field error={touched && error ? true : false} required={required}>
        {label && <label>{label}</label>}
        <div className={classes} style={this.getStyle()}>
          <DatePicker
            className={className}
            dateFormat={this.dateFormat}
            {...other}
            selected={this.selected}
            onBlur={this.onBlur}
            onFocus={this.onFocus}
            onChangeRaw={this.handleChange}
            onSelect={this.onSelect}
            popoverAttachment="bottom center"
            popoverTargetAttachment="top center"
            tabIndex={tabIndex}
            placeholderText={placeholder}
            disabled={disabled}
            autocomplete="off"
            customInput={customInput}
            {...rest}
            strictParsing={strictParsing}
          />
        </div>
        <div style={{ height: '10px' }}>
          {touched && error && (
            <div className={'input--message-error'}>{error}</div>
          )}
        </div>
      </Form.Field>
    );
  }
}

/**
 * Wrapper component for From - To date pickers
 */
export const RangeDatePickerField = ({
  fromName,
  toName,
  fromLabel,
  toLabel,
  fromDisabled,
  toDisabled,
  fluid,
  fromProps,
  toProps,
  ...rest
}) => {
  return (
    <Grid padded={false}>
      <Grid.Row columns={2}>
        <Grid.Column>
          <Field
            label={fromLabel}
            component={DatePickerField}
            name={fromName}
            fluid={fluid}
            disabled={fromDisabled}
            {...rest}
            {...fromProps}
          />
        </Grid.Column>
        <Grid.Column>
          <Field
            label={toLabel}
            component={DatePickerField}
            name={toName}
            fluid={fluid}
            disabled={toDisabled}
            {...rest}
            {...toProps}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

RangeDatePickerField.propTypes = {
  fromName: PropTypes.string.isRequired,
  toName: PropTypes.string.isRequired,
  fromLabel: PropTypes.string,
  toLabel: PropTypes.string,
  fluid: PropTypes.bool,
  fromDisabled: PropTypes.bool,
  toDisabled: PropTypes.bool,
};

RangeDatePickerField.defaultProps = {
  fromLabel: 'From:',
  toLabel: 'To:',
  fluid: true,
  fromDisabled: false,
  toDisabled: false,
};

/**
 * A wrapper component that allows user to enter a whole number and use a dropdown to select a fractional value
 */
export const IntegerFractionalField = (props) => {
  const {
    mode = InputMode.Integer,
    integerMaxLength,
    padded,
    inputWidth,
    dropdownWidth,
    inputProps,
    dropdownProps,
    input,
    label,
    required,
    type,
    fieldLabel,
    numericSize,
    handleBlur,
    allowNegative = false,
    hideSpinner = true,
    hideError = false,
    compact = false,
    loading = false,
    hideEmptyError = false,
    readOnly,
    showOnlyTouchedError = false,
    meta: { touched, error, asyncValidating, dispatch, form },
    ...rest
  } = props;
  const displayLabel = label && label !== ' ';

  const errorClassName = classNames('input--message', {
    'input--message-error': touched && error,
    'input--message-empty': !touched,
  });

  let errorMessage = <span className={errorClassName}>{error}</span>;
  if (
    hideError ||
    (!error && hideEmptyError) ||
    (error && showOnlyTouchedError && !touched)
  ) {
    errorMessage = null;
  }

  let wholeNumber = '';
  let fractional = '';
  let fractionalKey = '';

  if (input.value) {
    const value = String(input.value);
    [wholeNumber, fractional] = value.split('.');
  }

  if (fractional) {
    fractional = `.${fractional}`;
    Object.entries(FractionalEnum).forEach(([key, value]) => {
      if (Number(value) === Number(fractional)) {
        fractionalKey = key;
      }
    });
  }

  const proxiedInputProps = {
    ...input,
    ...rest,
    maxLength: integerMaxLength,
    required,
    type,
    readOnly,
    label: fieldLabel,
    onInput: (e) => {
      e.target.value = getValueFromInputMode(
        mode,
        numericSize || [12, 2],
        e.target.value
      );
    },
    onKeyPress: (e) => handleModeInputKeyPress(e, mode, allowNegative),
    onChange: (e, data) => {
      const isZero = data.value === '0';
      const numericValue = Number(data.value) || 0;
      const fractionalValue = Number(fractional) || 0;
      const value = _.add(numericValue, fractionalValue) || '';
      let stringValue = String(value);
      if (value < 1) {
        const fractional = stringValue.split('.')[1];
        stringValue = fractional
          ? isZero
            ? `0.${fractional}`
            : `.${fractional}`
          : '';
      }
      dispatch(reduxFormChange(form, input.name, stringValue));
    },
  };

  const proxiedDropdownProps = {
    onChange: (event, data) => {
      let fractional = 0;
      if (data.value) {
        fractional = FractionalEnum[data.value];
      }
      const value = add(Number(wholeNumber), Number(fractional), 4);
      dispatch(reduxFormChange(form, input.name, String(value)));
    },
  };

  return (
    <Form.Field
      error={touched && error ? true : false}
      required={required}
      className={`input--type-${type} ${compact ? 'input--compact' : ''}`}
    >
      {displayLabel && <label>{label}</label>}
      <Grid padded={padded}>
        <Grid.Row className="p-t-0 p-b-0">
          <Grid.Column width={inputWidth}>
            <InputComponent
              className={classNames({ 'read-only': readOnly })}
              {...proxiedInputProps}
              loading={loading || (!hideSpinner && asyncValidating)}
              autoComplete={'off'}
              onBlur={(e) => {
                input.onBlur(input.value);
                if (handleBlur) {
                  handleBlur(e, input.value);
                }
              }}
              value={wholeNumber}
            />
          </Grid.Column>
          <Grid.Column className="p-l-0" width={dropdownWidth}>
            <SelectComponent
              className={classNames({ 'read-only': readOnly })}
              options={FractionalDropdownOptions}
              {...proxiedDropdownProps}
              {...rest}
              value={fractionalKey}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {errorMessage}
    </Form.Field>
  );
};

IntegerFractionalField.propTypes = {
  inputWidth: PropTypes.number,
  dropdownWidth: PropTypes.number,
  padded: PropTypes.oneOf(['vertically', 'horizontally', false, true]),
  integerMaxLength: PropTypes.number,
};

IntegerFractionalField.defaultProps = {
  inputWidth: 8,
  dropdownWidth: 8,
  padded: 'vertically',
  integerMaxLength: 2,
};

/**
 *
 * Creates an event listener mapping hotkeys
 * handles numbers, strings, and functions that return a value
 * For more keyboard shortcuts visit: https://github.com/ianstormtaylor/is-hotkey
 * @example
 * // pressing ctrl+d will append °
 * // pressing ctrl+b will append '123'
 * const onKeydownHandler = createKeyboardShortcuts({ 'mod+d' : '°', 'mod+b': event => 123 });
 *
 */
const createKeyboardShortcuts = (keyboardShortcuts = {}) => {
  const hotkeys = Object.keys(keyboardShortcuts);
  return (e) => {
    const hotkey = hotkeys.find((hk) => isKeyHotkey(hk, e));
    if (hotkey) {
      const value = keyboardShortcuts[hotkey];

      switch (typeof value) {
        case 'function':
          const returnValue = value(e);
          if (returnValue) {
            e.target.value += String(returnValue);
          }
          break;
        case 'number':
        case 'string':
          e.target.value += String(value);
          break;
        default:
          break;
      }
      e.preventDefault();
    }
  };
};

// @todo remove inline styles
export const InputField = ({
  input,
  label,
  required,
  meta: { touched, error, asyncValidating, dispatch, form },
  mode,
  type,
  fieldLabel,
  numericSize,
  handleBlur,
  allowNegative = false,
  hideSpinner = true,
  hideError = false,
  formatOnBlur = false,
  compact = false,
  loading = false,
  hideEmptyError = false,
  readOnly,
  showOnlyTouchedError = false,
  keyboardShortcuts = {},
  inputLabelProps = {},
  infoMessage, // displays an info icon and displays the message text inside a popup
  ...rest
}) => {
  const inputProps = {
    ...input,
    ...rest,
    required,
    type,
    readOnly,
    label: fieldLabel,
    ...(Object.keys(keyboardShortcuts).length > 0 && {
      onKeyDown: createKeyboardShortcuts(keyboardShortcuts),
    }),
    onInput: (e) => {
      e.target.value = getValueFromInputMode(
        mode,
        numericSize || [12, 2],
        e.target.value
      );
    },
    onKeyPress: (e) => handleModeInputKeyPress(e, mode, allowNegative),
  };

  const displayLabel = label && label !== ' ';

  const errorClassName = classNames('input--message', {
    'input--message-error': touched && error,
    'input--message-empty': !touched,
  });

  let errorMessage = <span className={errorClassName}>{error}</span>;
  if (
    hideError ||
    (!error && hideEmptyError) ||
    (error && showOnlyTouchedError && !touched)
  ) {
    errorMessage = null;
  }

  return (
    <Form.Field
      error={touched && error ? true : false}
      required={required}
      className={`input--type-${type} ${compact ? 'input--compact' : ''}`}
    >
      {displayLabel && (
        <label>
          {label}
          {infoMessage && (
            <Popup
              style={{ zIndex: 1000000 }}
              content={infoMessage}
              trigger={<Icon name="info circle" />}
              on="hover"
            />
          )}
        </label>
      )}
      <InputComponent
        className={classNames({ 'read-only': readOnly })}
        {...inputProps}
        loading={loading || (!hideSpinner && asyncValidating)}
        autoComplete={'off'}
        onBlur={(e) => {
          // Always call the input's default blur as Redux-Form relies
          // on it for validations as well as determining if a field has been touched.
          const val = formatOnBlur
            ? formatOnBlur(e.target.value)
            : e.target.value;
          input.onBlur(val);
          if (handleBlur) {
            handleBlur(e, val);
          }
        }}
        {...inputLabelProps}
      />
      {errorMessage}
    </Form.Field>
  );
};

export const withReduxForm =
  (GenericInputComponent) =>
  ({
    input,
    label,
    required,
    meta: { touched, error, asyncValidating, dispatch, form, ...metaRest },
    mode,
    type,
    fieldLabel,
    numericSize,
    handleBlur,
    allowNegative = false,
    hideSpinner = true,
    hideError = false,
    formatOnBlur,
    compact = false,
    ...rest
  }) => {
    const inputProps = {
      ...input,
      ...rest,
      required,
      type,
      label: fieldLabel,
      onInput: (e) => {
        e.target.value = getValueFromInputMode(
          mode,
          numericSize || [12, 2],
          e.target.value
        );
      },
      onKeyPress: (e) => handleModeInputKeyPress(e, mode, allowNegative),
    };

    const displayLabel = label !== null && label !== ' ' && label !== false;

    const errorClassName = classNames('input--message', {
      'input--message-error': touched && error,
      'input--message-empty': !touched,
    });
    return (
      <Form.Field
        error={touched && error ? true : false}
        required={required}
        className={`input--type-${type} ${compact ? 'input--compact' : ''}`}
      >
        {displayLabel && <label>{label}</label>}
        <GenericInputComponent
          meta={{
            touched,
            error,
            asyncValidating,
            dispatch,
            form,
            ...metaRest,
          }}
          {...inputProps}
          loading={!hideSpinner && asyncValidating}
          autoComplete={'off'}
          onBlur={(e) => {
            // Always call the input's default blur as Redux-Form relies
            // on it for validations as well as determining if a field has been touched.
            if (e) {
              const val = formatOnBlur
                ? formatOnBlur(e.target.value)
                : e.target.value;
              input.onBlur(val);
              if (handleBlur) {
                handleBlur(e, val);
              }
            }
          }}
          formatOnBlur={formatOnBlur}
        />

        {hideError ? undefined : (
          <span className={errorClassName}>{error}</span>
        )}
      </Form.Field>
    );
  };

export const Input = ({
  input,
  required,
  meta: { touched, error },
  ...rest
}) => <InputComponent required={required} {...input} {...rest} />;

export const LabelInputField = ({
  input,
  required,
  meta: { touched, error },
  ...rest
}) => (
  <Form.Field error={touched && error ? true : false} required={required}>
    <InputComponent required={required} {...input} {...rest} />
    {touched && error ? (
      <span className={'input--message-error'}>{error}</span>
    ) : null}
  </Form.Field>
);

export class TextAreaField extends React.Component {
  static propTypes = {
    onChange: PropTypes.func,
  };

  static defaultProps = {
    onChange: () => {},
  };

  onChange = (...args) => {
    const { input, onChange } = this.props;

    input.onChange(...args);
    onChange(...args);
  };

  render() {
    const {
      input,
      label,
      required,
      meta: { touched, error },
      onChange,
      ...rest
    } = this.props;

    return (
      <Form.Field error={touched && error ? true : false} required={required}>
        <label>{label}</label>
        <TextAreaComponent
          required={required}
          {...input}
          {...rest}
          onChange={this.onChange}
          autoComplete={'off'}
        />
        {touched && error ? (
          <span className={'input--message-error'}>{error}</span>
        ) : null}
      </Form.Field>
    );
  }
}

export class DropzoneImage extends React.Component {
  static EMPTY_PICTURE = '';

  static propTypes = {
    /** Image string */
    displayImage: PropTypes.string,
    /** Redux form field name for the image string */
    displayImageName: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
  };

  render() {
    const {
      displayImage,
      displayImageName,
      input,
      required,
      meta: { touched, error, dispatch, form },
      disabled,
      ...rest
    } = this.props;

    return (
      <Form.Field error={touched && error ? true : false} required={required}>
        <ImageUploader
          displayImage={displayImage}
          onDrop={(files) => {
            if (files[0]) {
              input.onChange(files[0]);
            }
          }}
          onImageRemove={() => {
            input.onChange(DropzoneImage.EMPTY_PICTURE); // set preview to ''
            dispatch(
              reduxFormChange(
                form,
                displayImageName,
                DropzoneImage.EMPTY_PICTURE
              )
            ); // set image to ''
          }}
          disabled={disabled}
          {...rest}
        />
        {touched && error && (
          <span className={'input--message-error'}>{error}</span>
        )}
      </Form.Field>
    );
  }
}

export const TextArea = ({
  input,
  required,
  meta: { touched, error },
  ...rest
}) => <TextAreaComponent required={required} {...input} {...rest} />;

export const SelectField = ({
  input,
  upward,
  label,
  required,
  options,
  multiple = false,
  selectAllButton = false,
  selectNoneButton = false,
  meta: { touched, error, warning },
  ...custom
}) => {
  return (
    <Form.Field error={touched && error ? true : false} required={required}>
      {label && <label>{label}</label>}
      <SelectComponent
        upward={upward ? true : false}
        search
        value={
          input.value !== null && input.value !== undefined
            ? input.value
            : multiple
            ? []
            : ''
        }
        required={required}
        multiple={multiple}
        options={
          multiple && (selectAllButton || selectNoneButton)
            ? [getExtraButtons(), ...options]
            : options
        }
        onChange={(event, data) => {
          input.onChange(data.value);
        }}
        {...custom}
        autoComplete={'off'}
      />
      {touched && error ? (
        <span className={'input--message-error'}>{error}</span>
      ) : null}
      {touched && warning ? (
        <span className={'input--message-warning'}>{warning}</span>
      ) : null}
    </Form.Field>
  );

  function getExtraButtons() {
    return {
      key: -1,
      disabled: true,
      text: (
        <Fragment>
          {selectAllButton && input.value.length !== options.length && (
            <Button
              size="mini"
              onClick={(e) => {
                input.onChange([...options.map(({ value }) => value)]);
              }}
            >
              Select All
            </Button>
          )}
          {selectNoneButton && input.value.length !== 0 && (
            <Button
              size="mini"
              onClick={(e) => {
                input.onChange([]);
              }}
            >
              Clear Selected
            </Button>
          )}
        </Fragment>
      ),
    };
  }
};

export class Select extends React.Component {
  static propTypes = {
    input: PropTypes.object.isRequired,
    required: PropTypes.bool,
    options: PropTypes.array,
    usePortal: PropTypes.bool,
    meta: PropTypes.object.isRequired,
    label: PropTypes.string,
    onOutClick: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  };

  static defaultProps = {
    required: false,
    options: [],
    /** Only used if the parent container is an overflow: hidden but we don't want the dropdown to cut off */
    usePortal: false,
    label: '',
    onOutClick: () => {},
    onFocus: () => {},
    onBlur: () => {},
    onChange: () => {},
  };

  state = { open: false };

  onOpen = () => {
    this.setState({ open: true });
  };
  onClose = () => {
    this.setState({ open: false });
  };

  componentDidUpdate(prevProps) {
    /** Select first option if only one */
    const { options, onChange, input } = this.props;
    const { options: prevOptions } = prevProps;

    if (prevOptions !== options && options.length === 1) {
      onChange(options[0].value);
      input.onChange(options[0].value);
    }
  }

  render() {
    const { open } = this.state;
    const {
      input,
      required,
      options,
      usePortal,
      meta: { touched, error },
      label,
      onOutClick,
      fluid,
      onChange,
      onFocus,
      onBlur,
      ...rest
    } = this.props;

    const DropdownComponent = (
      <SelectComponent
        search
        value={input.value}
        required={required}
        options={options}
        onChange={(event, data) => {
          onChange(data.value);
          input.onChange(data.value);
        }}
        fluid={fluid}
        open={open}
        onOpen={this.onOpen}
        onClose={this.onClose}
        onFocus={(event) => {
          input.onFocus(event);
          onFocus(event);
        }}
        onBlur={(event) => {
          input.onBlur(event);
          input.onChange(input.value);
          onBlur(event);
        }}
        name={input.name}
        {...rest}
      />
    );

    return (
      <Form.Field error={touched && error ? true : false}>
        {label && <label>{label}</label>}

        {usePortal && open ? ( // only render the portal when the dropdown is activated
          <RelativePortal fluid={fluid} onOutClick={onOutClick}>
            {DropdownComponent}
          </RelativePortal>
        ) : (
          DropdownComponent
        )}
        {touched && error ? (
          <span className={'input--message-error'}>{error}</span>
        ) : null}
      </Form.Field>
    );
  }
}

export const SelectNative = ({
  input,
  options,
  fluid,
  disabled,
  label,
  meta: { touched, error },
}) => {
  const optionFields = options.map((option) => (
    <option key={option.key} value={option.value} className="item">
      {option.text}
    </option>
  ));

  return (
    <Form.Field error={touched && error ? true : false}>
      {label && <label>{label}</label>}
      <select
        name={input.name}
        value={input.value}
        className={classNames(
          'ui',
          'selection',
          'dropdown',
          fluid && 'fluid',
          disabled && 'disabled'
        )}
        onChange={(event) => input.onChange(event.currentTarget.value)}
        disabled={disabled}
      >
        {optionFields}
      </select>
      {touched && error ? (
        <span className={'input--message-error'}>{error}</span>
      ) : null}
    </Form.Field>
  );
};

export const ToggleField = ({ input, label, defaultChecked }) => (
  <Form.Field
    control={RadioComponent}
    toggle
    label={label}
    checked={input.value ? true : false}
    defaultChecked={defaultChecked}
    onClick={(event, data) => input.onChange(data.checked)}
  />
);

export const Toggle = ({
  input,
  label,
  defaultChecked,
  style,
  className,
  onClick,
  disabled,
  onChange,
}) => (
  <RadioComponent
    toggle
    label={label}
    checked={input.value ? true : false}
    defaultChecked={defaultChecked}
    onClick={(event, data) => {
      if (disabled) return;
      if (onClick) {
        onClick(event, data);
      } else {
        input.onChange(data.checked);
      }
      onChange(event, data.checked);
    }}
    style={style}
    className={className}
    disabled={disabled}
  />
);

Toggle.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  className: PropTypes.string,
  defaultChecked: PropTypes.bool,
  disabled: PropTypes.bool,
};

Toggle.defaultProps = {
  onChange: (e, checked) => {},
};

export const Text = ({ input, text = '' }) => {
  return input.value && text;
};

export class ButtonRadio extends React.Component {
  static propTypes = {
    label: PropTypes.string.isRequired,
    myValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  };

  handleChange = (e) => {
    const { input, myValue } = this.props;

    e.preventDefault();
    input.onChange(myValue);
  };

  render() {
    const { input, label, myValue } = this.props;

    return (
      <Form.Field>
        <Button
          basic
          className="white"
          active={input.value === myValue}
          onClick={this.handleChange}
        >
          {label}
        </Button>
      </Form.Field>
    );
  }
}

export class Radio extends React.Component {
  static propTypes = {
    input: PropTypes.shape({
      onChange: PropTypes.func.isRequired,
    }).isRequired,
    meta: PropTypes.shape({
      touched: PropTypes.bool,
      error: PropTypes.string,
    }),
    myValue: PropTypes.string.isRequired,
  };

  handleChange = (e, data) => {
    if (data.value === null) return;
    this.props.input.onChange(data.value);
  };

  render() {
    const {
      input,
      myValue,
      meta: { touched, error },
      label,
    } = this.props;

    return (
      <Form.Field error={touched && error ? true : false}>
        <div className="ui radio checkbox" style={{ width: 'auto' }}>
          <CheckboxComponent
            radio
            label={label}
            checked={input.value === myValue}
            onChange={this.handleChange}
            value={myValue}
          />
        </div>
        <div style={{ height: '10px' }}>
          {touched && error ? (
            <span className={'input--message-error'}>{error}</span>
          ) : null}
        </div>
      </Form.Field>
    );
  }
}

export const GroupedRadioField = ({
  className,
  input,
  options,
  label,
  horizontal = false,
  containerStyles = {},
}) => {
  return (
    <React.Fragment>
      {label && <label>{label}</label>}
      <div
        style={{
          display: 'flex',
          flexDirection: horizontal ? 'row' : 'column',
          ...containerStyles,
        }}
      >
        {options.map((option) => (
          <Form.Field key={option.value}>
            <RadioComponent
              className={classNames(
                'grouped-radio-input',
                { horizontal },
                className
              )}
              label={option.label}
              value={option.value}
              checked={input.value === option.value}
              onChange={(e, { value }) => input.onChange(value)}
            />
          </Form.Field>
        ))}
      </div>
    </React.Fragment>
  );
};

GroupedRadioField.propTypes = {
  input: PropTypes.object.isRequired,
  className: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    })
  ).isRequired,
  label: PropTypes.string.isRequired,
};

export const RadioField = ({
  input,
  label,
  meta: { touched, error },
  ...custom
}) => (
  <Form.Field
    control={RadioComponent}
    label={label}
    checked={input.value ? true : false}
    onClick={(event, data) => input.onChange(!data.checked)}
    {...custom}
  />
);

export const Checkbox = ({
  input,
  label,
  meta: { touched, error },
  ...custom
}) => (
  <CheckboxComponent
    label={label}
    checked={input.value ? true : false}
    onClick={(event, data) => input.onChange(data.checked)}
    {...custom}
  />
);

export const CheckboxField = ({
  input,
  label,
  meta: { touched, error },
  useYN = false, // should use Y or N
  required = false,
  useInputLabel = false, // use the stylings of an input label
  disabled = false,
  infoMessage,
  ...custom
}) => {
  let checked = false;
  if (useYN) {
    checked = input.value === Y_N_BIT.Y;
  } else {
    checked = input.value ? true : false;
  }
  return (
    <Form.Field error={touched && error ? true : false} required={required}>
      <CheckboxComponent
        className={classNames({ 'checkbox-input-label': useInputLabel })}
        label={label}
        checked={checked}
        onClick={(event, data) => {
          if (disabled) {
            return;
          }
          if (useYN) {
            const isYes = _.toUpper(input.value) === Y_N_BIT.Y;
            isYes ? input.onChange(Y_N_BIT.N) : input.onChange(Y_N_BIT.Y);
          } else {
            input.onChange(!input.value);
          }
        }}
        disabled={disabled}
        {...custom}
      />
      {touched && error && (
        <span className={'input--message-error'}>{error}</span>
      )}
      {infoMessage && (
        <Popup
          style={{ zIndex: 1000000 }}
          content={infoMessage}
          trigger={<Icon name="info circle" />}
          on="hover"
        />
      )}
    </Form.Field>
  );
};

export const RangeField = ({
  input,
  label,
  min,
  max,
  required,
  meta: { touched, error },
  ...rest
}) => (
  <Form.Field error={touched && error ? true : false} required={required}>
    <label>
      {label} : {input.value}
    </label>
    <input
      type="range"
      required={required}
      min={min}
      max={max}
      {...input}
      {...rest}
    />
    {touched && error ? <div style={{ color: '#9F3A38' }}>{error}</div> : null}
  </Form.Field>
);

export const Range = ({
  input,
  min,
  max,
  required,
  meta: { touched, error },
  ...rest
}) => (
  <input
    type="range"
    required={required}
    min={min}
    max={max}
    {...input}
    {...rest}
  />
);

export const DropdownField = ({
  input,
  label,
  required,
  options,
  meta: { touched, error },
  ...custom
}) => (
  <Form.Field error={touched && error ? true : false} required={required}>
    <label>{label}</label>
    <DropdownComponent
      search
      value={input.value}
      required={required}
      options={options}
      onChange={(event, data) => input.onChange(data.value)}
      {...custom}
    />
    {touched && error ? (
      <span className={'input--message-error'}>{error}</span>
    ) : null}
  </Form.Field>
);

export const Dropdown = ({
  input,
  required,
  options,
  meta: { touched, error },
  ...rest
}) => (
  <DropdownComponent
    search
    value={input.value}
    required={required}
    options={options}
    onChange={(event, data) => input.onChange(data.value)}
    {...rest}
  />
);

export const NumericField = (props) => {
  const parse =
    props.mode === InputMode.Decimal ? formParseDecimal : formParseInt;
  return <Field {...props} parse={parse} />;
};

export const FilePickerButton = ({ onFileSelect, ...rest }) => {
  return (
    <Button
      onClick={(e) =>
        e.target.firstChild ? e.target.firstChild.click() : undefined
      }
      {...rest}
    >
      <input
        style={{ display: 'none' }}
        type="file"
        onChange={(e) => {
          onFileSelect(e.target.files);
          e.target.value = '';
        }}
      />
      Select File
    </Button>
  );
};

export const LabeledInput = ({ label, children, error, skipInput = false }) => {
  return (
    <div className={'ui form'}>
      <div className={skipInput ? '' : 'ui input'}>
        <div className={'field'}>
          <label>{label}</label>
          {children}
        </div>
      </div>
      <div>
        {error && (
          <span className={'text-red input--message-error'}>{error}</span>
        )}
      </div>
    </div>
  );
};

export const PopupField = ({
  input,
  required,
  options,
  meta: { touched, error },
  label,
  onOutClick,
  fluid,
  style,
  ...rest
}) => {
  return (
    <Form.Field error={touched && error ? true : false}>
      {label && <label>{label}</label>}
      <Popup
        trigger={<span style={style}>{input.value}</span>}
        content={input.value}
        on="hover"
        {...rest}
      />
      {touched && error ? (
        <span className={'input--message-error'}>{error}</span>
      ) : null}
    </Form.Field>
  );
};

export const TextField = ({ showPopup = false, ...props }) => {
  return <PopupField disabled={!showPopup} {...props} />;
};

export class ButtonGroupField extends React.Component {
  static propTypes = {
    options: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([
          PropTypes.bool,
          PropTypes.string,
          PropTypes.number,
        ]),
      })
    ),
    unsettable: PropTypes.bool,
    fluid: PropTypes.bool,
  };

  static defaultProps = {
    unsettable: false,
    onChange: (value) => {},
    fluid: false,
  };

  handleChange = (value, e) => {
    const { unsettable, input } = this.props;

    if (unsettable && value === input.value) {
      input.onChange('');
      input.onBlur();
    } else {
      input.onChange(value);
    }
  };

  render() {
    const {
      options,
      label,
      className,
      disabled,
      type,
      input,
      fluid,
      meta: { touched, error },
      required,
    } = this.props;

    const displayLabel = label != null && label.length > 0;

    const errorClassName = classNames('input--message', {
      'input--message-error': touched && error,
      error: touched && error,
      'input--message-empty': !touched,
    });
    const labelClassName = classNames({ error: touched && error });

    return (
      <Form.Field error={touched && error} required={required}>
        {displayLabel && <label className={labelClassName}>{label}</label>}
        <Button.Group
          fluid={fluid}
          basic
          className={classNames(className, 'btn-group--active-group')}
        >
          {options.map((option, i) => {
            return (
              <Button
                basic
                active={option.value === input.value}
                className="white"
                disabled={option.disabled || disabled}
                key={i}
                type={type}
                onClick={(e) => this.handleChange(option.value, e)}
              >
                {option.text}
              </Button>
            );
          })}
        </Button.Group>
        {error && <span className={errorClassName}>{error}</span>}
      </Form.Field>
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/SemanticWrapper.js